import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { TriangleAlertIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AuthShell } from '@/components/layout/AuthShell'
import { Spinner } from '@/components/Spinner'
import { userManager } from '@/auth/userManager'

/**
 * Where Keycloak redirects back to after login. Exchanges the authorization code for tokens and
 * then hands over to the app.
 */
export function CallbackPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // A flag rather than the message itself: a state that held a translated string would keep
  // showing the old language after a switch.
  const [failed, setFailed] = useState(false)

  // The code and its OIDC state are single-use and are consumed by the first call. React's
  // StrictMode runs effects twice in development, so without this guard the second run would
  // fail with "No matching state found in storage" on every single login.
  const handled = useRef(false)

  useEffect(() => {
    if (handled.current) {
      return
    }
    handled.current = true

    const params = new URLSearchParams(window.location.search)
    if (!params.has('code') && !params.has('error')) {
      // Opened without a Keycloak response at all — a bookmark, a manual reload, the back button
      // after logout. Start over rather than show an error the user cannot act on.
      navigate('/login', { replace: true })
      return
    }

    void (async () => {
      try {
        const user = await userManager.signinRedirectCallback()
        const returnTo = (user.state as { returnTo?: string } | undefined)?.returnTo
        navigate(returnTo && returnTo !== '/login' ? returnTo : '/', { replace: true })
      } catch (cause) {
        console.error('Login callback failed', cause)
        setFailed(true)
      }
    })()
  }, [navigate])

  if (failed) {
    return (
      <AuthShell>
        <Card>
          <CardHeader>
            <TriangleAlertIcon className="text-destructive mb-1 size-5" aria-hidden />
            <CardTitle>{t('auth.failedTitle')}</CardTitle>
            <CardDescription>{t('auth.failedDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2">
            <Button className="w-full" onClick={() => navigate('/login', { replace: true })}>
              {t('auth.retry')}
            </Button>
            {/* The most likely reason somebody ends up here twice in a row. */}
            <Button asChild variant="ghost" className="w-full">
              <Link to="/forgot-password">{t('forgotPassword.link')}</Link>
            </Button>
          </CardContent>
        </Card>
      </AuthShell>
    )
  }
  return <Spinner label={t('auth.completing')} />
}
